import { Button, Grid, Typography, Alert } from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { isObjectEmpty } from "../../util/commonUtils";
import StarsCalendar from "./StarsCalendar";
import { getTotalAllotments, isHoliday } from "../../util/bid/timeOffBidUtils";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import PageLoading from "../PageLoading";
import { isAwardedBid, isDeletedBid } from "../../util/bid/bidCommon";
import { getEligibleEmployees, getIneligibleEmployees } from "../../util/bid/bidGroupUtils";
import { getDayStateForNCBidAllotment } from "../../util/calendar";
import NCBidAllotment from "./NCBidAllotment";
import NCBidAllotmentDialog from "./NCBidAllotmentDialog";
import AllotmentsOverrideDialog from "./AllotmentsOverrideDialog";
import { convertIsoDateToLuxon } from "../../util/dateTimeFormatter";
import { getThanksGivingDateOfTheYear } from "../../util/dateHelper";

const ManageNCBidAllotments = (props) => {
  const [isAllotmentDialogOpen, setIsAllotmentDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [isRecalculateDialogOpen, setIsRecalculateDialogOpen] = useState(false);
  const [isRecalculateLoading, setIsRecalculateLoading] = useState(false);
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);

  const handleAllotmentDialogOpen = (dayState) => {
    if (dayState.date && dayState.amOrPmCount >= 0 && props.schedMonth !== "" && props.schedYear !== "") {
      const dateObject = convertIsoDateToLuxon(dayState.date);
      const converted_year = parseInt(props.schedYear);
      const thanksgivingDate = getThanksGivingDateOfTheYear(converted_year);

      const selectedDayIsHoliday = isHoliday(dateObject, converted_year, thanksgivingDate);
      if (!selectedDayIsHoliday) {
        setSelectedDate(dayState.date);
        setIsAllotmentDialogOpen(true);
      }
    }
  };

  const handleRecalculateDialogRecalculateClick = () => {
    setIsRecalculateLoading(true);
    const calcDailyAllotments = {
      amOrPmCount: 1, // We are setting default value of 1 for NC Bids
    };
    props.setCalcDailyAllotments(calcDailyAllotments);
    props.setIsSchedMonthOrYearChanged(false);
    props.setAllotmentDayState(getDayStateForNCBidAllotment(props.schedYear, props.schedMonth, calcDailyAllotments));
    setIsRecalculateDialogOpen(false);
    setIsRecalculateLoading(false);
  };

  const renderBidEffectiveDatesChanged = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          The Bid Effective Month/Year has changed. Please click Recalculate to update the allotments.
        </Alert>
      </Grid>
    );
  };

  return (
    <>
      {props.isBidAllotmentsLoading || props.isBidGroupLoading ? (
        <PageLoading />
      ) : (
        <div data-testid="manage-nc-bid-allotments">
          {props.isSchedMonthOrYearChanged && renderBidEffectiveDatesChanged()}
          <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
            <Grid container sx={{ padding: "10px" }}>
              <Grid item xs={8}>
                <Typography
                  variant="h6"
                  sx={{ textAlign: "left", marginTop: "10px", marginLeft: "10px", fontWeight: "bold" }}
                >
                  {props.bid.station} | {props.bid.workgroup}
                  {props.schedMonth !== "" &&
                    props.schedYear !== "" &&
                    ` | ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
                  {props.bidName ? ` | ${props.bidName}` : ""}
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                container
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "right",
                  paddingRight: "1rem",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  sx={{
                    marginRight: "10px",
                  }}
                  onClick={() => {
                    setIsOverrideDialogOpen(true);
                  }}
                  disabled={
                    props.schedMonth === "" ||
                    props.schedYear === "" ||
                    props.bidGroup.length === 0 ||
                    isAwardedBid(props.bid.status) ||
                    isDeletedBid(props.bid.status)
                  }
                >
                  Override
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={() => {
                    setIsRecalculateDialogOpen(true);
                  }}
                  disabled={
                    props.schedMonth === "" ||
                    props.schedYear === "" ||
                    props.bidGroup.length === 0 ||
                    isAwardedBid(props.bid.status) ||
                    isDeletedBid(props.bid.status)
                  }
                >
                  Recalculate
                </Button>
              </Grid>
            </Grid>
            <>
              <Grid container sx={{ backgroundColor: "secondary.swaLightestBlue" }}>
                <Grid item xs={6}>
                  <Grid container spacing={2} sx={{ padding: "10px" }}>
                    <Grid item>Total Employees: </Grid>
                    <Grid item sx={{ fontWeight: "bold" }}>
                      {getEligibleEmployees(props.bidGroup).length + getIneligibleEmployees(props.bidGroup).length}
                    </Grid>
                    <Grid item>Total Allotments:</Grid>

                    <Grid item sx={{ fontWeight: "bold" }}>
                      {getTotalAllotments(props.allotmentDayState)}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <Grid container spacing={2} justifyContent="flex-end" sx={{ padding: "10px" }}>
                    <Grid item> Calculated Daily Allotments: </Grid>
                    <Grid item sx={{ fontWeight: "bold" }}>
                      {isObjectEmpty(props.allotmentDayState) ? "NA" : props.calcDailyAllotments.amOrPmCount}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {props.bidGroup?.length === 0 || props.schedMonth === "" || props.schedYear === "" ? (
                <Grid
                  container
                  sx={{ minHeight: "280px", color: "secondary.swaBlue", padding: "1rem", textAlign: "left" }}
                >
                  <Grid item xs={12} sx={{ color: "primary.swaBlue" }}>
                    <Grid>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Please select the {props.bid.bidType} month + year and load the Bid Group to view the
                        allotments.
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid container>
                  <StarsCalendar
                    dayState={props.allotmentDayState}
                    DayComponent={NCBidAllotment}
                    handleDialogOpen={handleAllotmentDialogOpen}
                    isLoading={props.isBidAllotmentsLoading || props.isBidGroupLoading}
                  />
                </Grid>
              )}
            </>
          </Grid>
          <Grid sx={{ border: "40px" }}>
            <NCBidAllotmentDialog
              isAllotmentDialogOpen={isAllotmentDialogOpen}
              allotmentDayState={props.allotmentDayState}
              date={selectedDate}
              setAllotmentDayState={props.setAllotmentDayState}
              setIsAllotmentDialogOpen={setIsAllotmentDialogOpen}
              setCalcDailyAllotments={props.setCalcDailyAllotments}
              calcDailyAllotments={props.calcDailyAllotments}
              bid={props.bid}
              schedMonth={props.schedMonth}
              schedYear={props.schedYear}
            />
          </Grid>
          {isRecalculateDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isRecalculateDialogOpen}
              title={"Recalculate Allotments"}
              message={""}
              hasWarning={true}
              warningMessage={"This will override any edits made to the allotments."}
              yesTitle={"Recalculate"}
              noTitle={"Cancel"}
              handleYesButtonClicked={handleRecalculateDialogRecalculateClick}
              handleNoButtonClicked={() => {
                setIsRecalculateDialogOpen(false);
              }}
              isYesLoading={isRecalculateLoading}
            />
          )}
          {isOverrideDialogOpen && (
            <AllotmentsOverrideDialog
              isDialogOpen={isOverrideDialogOpen}
              onClickDialogClose={() => setIsOverrideDialogOpen(false)}
              allotmentDayState={props.allotmentDayState}
              setAllotmentDayState={props.setAllotmentDayState}
              schedMonth={props.schedMonth}
              schedYear={props.schedYear}
            />
          )}
        </div>
      )}
    </>
  );
};

ManageNCBidAllotments.propTypes = {
  bid: PropTypes.object.isRequired,
  bidGroup: PropTypes.array.isRequired,
  schedMonth: PropTypes.string.isRequired,
  schedYear: PropTypes.string.isRequired,
  isBidAllotmentsLoading: PropTypes.bool.isRequired,
  isBidGroupLoading: PropTypes.bool.isRequired,
  calcDailyAllotments: PropTypes.object.isRequired,
  setCalcDailyAllotments: PropTypes.func.isRequired,
  allotmentDayState: PropTypes.array.isRequired,
  setAllotmentDayState: PropTypes.func.isRequired,
  isSchedMonthOrYearChanged: PropTypes.bool.isRequired,
  setIsSchedMonthOrYearChanged: PropTypes.func.isRequired,
  bidName: PropTypes.string,
};

export default ManageNCBidAllotments;
